import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { getPartnerWhatsappDealerHealth } from "../../api/partnerPortalApi";
import PartnerWhatsappDealerHealthTable from "../../components/partner/whatsapp/PartnerWhatsappDealerHealthTable";
import PartnerWhatsappExportButton from "../../components/partner/whatsapp/PartnerWhatsappExportButton";
import PartnerWhatsappKpiCard from "../../components/partner/whatsapp/PartnerWhatsappKpiCard";
import { usePartnerPortalBase } from "../../components/partner/PartnerPortalBaseProvider";
import {
  PARTNER_WHATSAPP_DEALER_HEALTH_SORT_OPTIONS,
  PARTNER_WHATSAPP_DEALER_HEALTH_STATUS_FILTERS,
  PARTNER_WHATSAPP_RANGE_OPTIONS,
} from "../../constants/partnerWhatsappMonitoringConstants";
import {
  buildDealerHealthSearchParams,
  dealerHealthHasActiveFilters,
  parseDealerHealthSearchParams,
} from "../../utils/partnerWhatsappDealerHealthViewModel";
import { formatPartnerWhatsappCount } from "../../utils/partnerWhatsappSummaryViewModel";
import { exportPartnerWhatsappDealerHealthCsv } from "../../utils/partnerWhatsappExport";
import "../../styles/partnerPortalDashboard.css";

function DealerHealthSkeleton() {
  return (
    <div className="partner-dashboard partner-dashboard-skeleton" aria-busy="true">
      <div className="partner-skeleton-block partner-skeleton-hero" />
      <div className="partner-kpi-grid">
        {[1, 2, 3, 4].map((id) => (
          <div key={id} className="partner-skeleton-block partner-skeleton-kpi" />
        ))}
      </div>
      <div className="partner-skeleton-block" style={{ minHeight: "18rem" }} />
    </div>
  );
}

export default function PartnerWhatsappDealerHealthPage() {
  const { path } = usePartnerPortalBase();
  const [searchParams, setSearchParams] = useSearchParams();
  const filters = useMemo(() => parseDealerHealthSearchParams(searchParams), [searchParams]);
  const whatsappPath = path("whatsapp");

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");
  const [searchInput, setSearchInput] = useState(filters.search);

  const loadDealerHealth = useCallback(async () => {
    setLoading(true);
    setErrorMessage("");
    try {
      const body = await getPartnerWhatsappDealerHealth(filters);
      setData(body);
    } catch {
      setData(null);
      setErrorMessage("We could not load dealer WhatsApp health right now.");
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    loadDealerHealth();
  }, [loadDealerHealth]);

  useEffect(() => {
    setSearchInput(filters.search);
  }, [filters.search]);

  const updateFilters = (patch) => {
    const nextFilters = { ...filters, ...patch, page: patch.page ?? 1 };
    setSearchParams(buildDealerHealthSearchParams(nextFilters), { replace: true });
  };

  const onSearchSubmit = (event) => {
    event.preventDefault();
    updateFilters({ search: searchInput.trim() });
  };

  const hasActiveFilters = dealerHealthHasActiveFilters(filters);
  const summary = data?.summary || {};
  const pagination = data?.pagination || { page: 1, pageSize: 25, total: 0, totalPages: 0 };
  const isEmptyNetwork = (summary.totalDealers || 0) === 0 && !hasActiveFilters;

  if (loading) {
    return <DealerHealthSkeleton />;
  }

  if (errorMessage) {
    return (
      <div className="partner-panel partner-whatsapp-page">
        <header className="partner-dashboard-hero">
          <div>
            <h2>Dealer WhatsApp Health</h2>
            <p className="partner-dashboard-hero__intro">
              Check which dealers are ready for brokered WhatsApp leads.
            </p>
          </div>
        </header>
        <div className="partner-panel">
          <p className="admin-data-health-error">{errorMessage}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="partner-dashboard partner-whatsapp-page">
      <header className="partner-dashboard-hero">
        <div>
          <p className="partner-dashboard-hero__eyebrow">
            <Link to={whatsappPath}>WhatsApp Leads</Link>
          </p>
          <h2>Dealer WhatsApp Health</h2>
          <p className="partner-dashboard-hero__intro">
            Check which dealers are ready for brokered WhatsApp leads.
          </p>
        </div>
        <div className="partner-whatsapp-controls">
          <Link className="partner-dashboard-btn" to={path("whatsapp/conversations")}>
            View conversations
          </Link>
          <PartnerWhatsappExportButton
            label="Export dealer health CSV"
            onExport={() => exportPartnerWhatsappDealerHealthCsv(filters)}
          />
          <button type="button" className="partner-dashboard-btn" onClick={loadDealerHealth}>
            Refresh
          </button>
        </div>
      </header>

      <div className="partner-kpi-grid">
        <PartnerWhatsappKpiCard
          icon="🏬"
          label="Total dealers"
          value={formatPartnerWhatsappCount(summary.totalDealers)}
          tone="blue"
        />
        <PartnerWhatsappKpiCard
          icon="✅"
          label="Ready"
          value={formatPartnerWhatsappCount(summary.ready)}
          tone="green"
        />
        <PartnerWhatsappKpiCard
          icon="⚠️"
          label="Needs attention"
          value={formatPartnerWhatsappCount(summary.needsAttention)}
          tone="amber"
        />
        <PartnerWhatsappKpiCard
          icon="📵"
          label="Missing WhatsApp"
          value={formatPartnerWhatsappCount(summary.missingWhatsapp)}
          tone="red"
        />
        <PartnerWhatsappKpiCard
          icon="☎️"
          label="Invalid phone"
          value={formatPartnerWhatsappCount(summary.invalidPhone)}
          tone="red"
        />
        <PartnerWhatsappKpiCard
          icon="🔄"
          label="Not synced"
          value={formatPartnerWhatsappCount(summary.notSynced)}
          tone="amber"
        />
        <PartnerWhatsappKpiCard
          icon="💬"
          label="Low response"
          value={formatPartnerWhatsappCount(summary.lowResponse)}
          tone="amber"
        />
        <PartnerWhatsappKpiCard
          icon="📞"
          label="Manual outreach heavy"
          value={formatPartnerWhatsappCount(summary.manualOutreachHeavy)}
          tone="amber"
        />
      </div>

      <section className="partner-panel partner-whatsapp-filters">
        <form className="partner-health-toolbar" onSubmit={onSearchSubmit}>
          <label>
            Range
            <select
              value={filters.range}
              onChange={(event) => updateFilters({ range: event.target.value })}
              aria-label="Dealer health range"
            >
              {PARTNER_WHATSAPP_RANGE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
          <label>
            Health status
            <select
              value={filters.status}
              onChange={(event) => updateFilters({ status: event.target.value })}
              aria-label="Dealer health status"
            >
              {PARTNER_WHATSAPP_DEALER_HEALTH_STATUS_FILTERS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
          <label>
            Sort
            <select
              value={filters.sort}
              onChange={(event) => updateFilters({ sort: event.target.value })}
              aria-label="Dealer health sort"
            >
              {PARTNER_WHATSAPP_DEALER_HEALTH_SORT_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
          <label>
            Direction
            <select
              value={filters.direction}
              onChange={(event) => updateFilters({ direction: event.target.value })}
              aria-label="Dealer health sort direction"
            >
              <option value="desc">Descending</option>
              <option value="asc">Ascending</option>
            </select>
          </label>
          <label className="partner-health-toolbar__search">
            Search dealer
            <input
              type="search"
              value={searchInput}
              onChange={(event) => setSearchInput(event.target.value)}
              placeholder="Dealer name or city"
            />
          </label>
          <button type="submit" className="partner-dashboard-btn">
            Search
          </button>
        </form>
      </section>

      {isEmptyNetwork ? (
        <section className="partner-panel partner-whatsapp-empty">
          <h3>Dealer WhatsApp health will appear after dealers are connected to brokered WhatsApp monitoring.</h3>
        </section>
      ) : null}

      {!isEmptyNetwork && !data?.items?.length ? (
        <section className="partner-panel partner-whatsapp-empty">
          <p className="muted">No dealers found for this filter.</p>
        </section>
      ) : null}

      <PartnerWhatsappDealerHealthTable items={data?.items || []} />

      {pagination.totalPages > 1 ? (
        <div className="partner-health-pagination">
          <button
            type="button"
            className="partner-dashboard-btn"
            disabled={pagination.page <= 1}
            onClick={() => updateFilters({ page: pagination.page - 1 })}
          >
            Previous
          </button>
          <span className="muted">
            Page {pagination.page} of {pagination.totalPages}
          </span>
          <button
            type="button"
            className="partner-dashboard-btn"
            disabled={pagination.page >= pagination.totalPages}
            onClick={() => updateFilters({ page: pagination.page + 1 })}
          >
            Next
          </button>
        </div>
      ) : null}
    </div>
  );
}
